import axios from 'axios';
import {
    BUSINESS_ORGANISATION_LIST_REQUEST,
    BUSINESS_ORGANISATION_LIST_SUCCESS,
    BUSINESS_ORGANISATION_LIST_FAIL,
    BUSINESS_ORGANISATION_DETAILS_REQUEST,
    BUSINESS_ORGANISATION_DETAILS_SUCCESS,
    BUSINESS_ORGANISATION_DETAILS_FAIL,
    BUSINESS_ORGANISATION_CREATE_REQUEST,
    BUSINESS_ORGANISATION_CREATE_SUCCESS,
    BUSINESS_ORGANISATION_CREATE_FAIL,
    BUSINESS_ORGANISATION_UPDATE_REQUEST,
    BUSINESS_ORGANISATION_UPDATE_SUCCESS,
    BUSINESS_ORGANISATION_UPDATE_FAIL,
    BUSINESS_ORGANISATION_DELETE_REQUEST,
    BUSINESS_ORGANISATION_DELETE_SUCCESS,
    BUSINESS_ORGANISATION_DELETE_FAIL,
    BUSINESS_ORGANISATION_DTO_REQUEST,
    BUSINESS_ORGANISATION_DTO_SUCCESS,
    BUSINESS_ORGANISATION_DTO_FAIL,
    BUSINESS_ORGANISATION_LIST_BYIDS_REQUEST,
    BUSINESS_ORGANISATION_LIST_BYIDS_SUCCESS,
    BUSINESS_ORGANISATION_LIST_BYIDS_FAIL,
    BUSINESS_ORGANISATION_ACTIVATE_REQUEST,
    BUSINESS_ORGANISATION_ACTIVATE_SUCCESS,
    BUSINESS_ORGANISATION_ACTIVATE_FAIL,
    BUSINESS_ORGANISATION_DEACTIVATE_REQUEST,
    BUSINESS_ORGANISATION_DEACTIVATE_SUCCESS,
    BUSINESS_ORGANISATION_DEACTIVATE_FAIL,
    BUSINESS_ORGANISATION_AGREEMENT_UPDATE_REQUEST,
    BUSINESS_ORGANISATION_AGREEMENT_UPDATE_SUCCESS,
    BUSINESS_ORGANISATION_AGREEMENT_UPDATE_FAIL,
    SEND_EMAIL_REQUEST,
    SEND_EMAIL_SUCCESS,
    SEND_EMAIL_FAIL,
} from '../constants/businessOrganisationConstants';

// Helper functions
const getAuthConfig = (token) => ({
    headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`
    }
});

const formatErrorMessage = (error) => {
    const errorMessages = [];
    if (error.response?.data) {
        if (error.response.data.errors) {
            Object.entries(error.response.data.errors).forEach(([key, value]) => {
                errorMessages.push(`${key}: ${value[0]}`);
            });
        }
        if (error.response.data.detail) {
            errorMessages.push(`Detail: ${error.response.data.detail}`);
        }
        if (error.response.data.message) {
            errorMessages.push(`Message: ${error.response.data.message}`);
        }
    }
    return errorMessages.length > 0 ? errorMessages : [error.message || "An error occurred, please try again later"];
};

const API_BASE_URL = '/dotnet_api/v1/businessOrganisation';

// Action creators
export const listBusinessOrganisations = (filter) => async (dispatch, getState) => {
    try {
        dispatch({ type: BUSINESS_ORGANISATION_LIST_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const config = {
            ...getAuthConfig(userInfo.token),
            params: filter || {}
        };
        
        const { data } = await axios.get(`${API_BASE_URL}/all`, config);
        dispatch({ type: BUSINESS_ORGANISATION_LIST_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: BUSINESS_ORGANISATION_LIST_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const listBusinessOrganisationsByIds = (ids) => async (dispatch, getState) => {
    try {
        dispatch({ type: BUSINESS_ORGANISATION_LIST_BYIDS_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.post(
            `${API_BASE_URL}/ids/all`,
            ids,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: BUSINESS_ORGANISATION_LIST_BYIDS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: BUSINESS_ORGANISATION_LIST_BYIDS_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const loadBusinessOrganisationDto = () => async (dispatch, getState) => {
    try {
        dispatch({ type: BUSINESS_ORGANISATION_DTO_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.get(
            `${API_BASE_URL}/dto`,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: BUSINESS_ORGANISATION_DTO_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: BUSINESS_ORGANISATION_DTO_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const getBusinessOrganisationDetails = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: BUSINESS_ORGANISATION_DETAILS_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.get(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: BUSINESS_ORGANISATION_DETAILS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: BUSINESS_ORGANISATION_DETAILS_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const deleteBusinessOrganisation = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: BUSINESS_ORGANISATION_DELETE_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        await axios.delete(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: BUSINESS_ORGANISATION_DELETE_SUCCESS });
    } catch (error) {
        dispatch({
            type: BUSINESS_ORGANISATION_DELETE_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const createBusinessOrganisation = (businessOrganisation) => async (dispatch, getState) => {
    try {
        dispatch({ type: BUSINESS_ORGANISATION_CREATE_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.post(
            API_BASE_URL,
            businessOrganisation,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: BUSINESS_ORGANISATION_CREATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: BUSINESS_ORGANISATION_CREATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const updateBusinessOrganisation = (id, changes) => async (dispatch, getState) => {
    try {
        dispatch({ type: BUSINESS_ORGANISATION_UPDATE_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.patch(
            `${API_BASE_URL}/${id}`,
            changes,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: BUSINESS_ORGANISATION_UPDATE_SUCCESS, payload: data });
        dispatch({ type: BUSINESS_ORGANISATION_DETAILS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: BUSINESS_ORGANISATION_UPDATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const activateBusinessOrganisation = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: BUSINESS_ORGANISATION_ACTIVATE_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.post(
            `${API_BASE_URL}/${id}/reactivate`,
            {},
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: BUSINESS_ORGANISATION_ACTIVATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: BUSINESS_ORGANISATION_ACTIVATE_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const deactivateBusinessOrganisation = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: BUSINESS_ORGANISATION_DEACTIVATE_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.post(
            `${API_BASE_URL}/${id}/deactivate`,
            {},
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: BUSINESS_ORGANISATION_DEACTIVATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: BUSINESS_ORGANISATION_DEACTIVATE_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const updateAgreement = (id, agreementData) => async (dispatch, getState) => {
    try {
        dispatch({ type: BUSINESS_ORGANISATION_AGREEMENT_UPDATE_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.patch(
            `${API_BASE_URL}/${id}/agreement`,
            agreementData,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: BUSINESS_ORGANISATION_AGREEMENT_UPDATE_SUCCESS, payload: data });
        dispatch({ type: BUSINESS_ORGANISATION_DETAILS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: BUSINESS_ORGANISATION_AGREEMENT_UPDATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const sendEmail = (content) => async (dispatch, getState) => {
    try {
        dispatch({ type: SEND_EMAIL_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.post(
            '/dotnet_api/v1/auth/sendmail',
            content,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: SEND_EMAIL_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: SEND_EMAIL_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
}; 